import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppDispatch } from '../store/store';
import { setFlags } from '../store/flagsSlice';

const parseBool = (v: string | null): boolean | undefined => {
  if (v == null) return undefined;
  const s = v.trim().toLowerCase();
  if (['1', 'true', 'on', 'yes'].includes(s)) return true;
  if (['0', 'false', 'off', 'no'].includes(s)) return false;
  if (s === '') return true; // bare ?flag
  return undefined;
};

export const readFlagsFromQuery = (search: string) => {
  const q = new URLSearchParams(search);
  const devMode = q.has('devMode') ? parseBool(q.get('devMode')) : undefined;
  const pttHidden = q.has('pttHidden') ? parseBool(q.get('pttHidden')) : undefined;

  // Only enable demoMode if both env var is true AND query param is true
  let demoMode: boolean | undefined = undefined;
  if (q.has('demoMode')) {
    const queryDemoMode = parseBool(q.get('demoMode'));
    const allowDemoMode = import.meta.env.VITE_ALLOW_DEMO_MODE === 'true';
    demoMode = queryDemoMode === true && allowDemoMode;
  }

  return { devMode, pttHidden, demoMode };
};

export const bootstrapFlagsOnce = (dispatch: AppDispatch) => {
  const { devMode, pttHidden, demoMode } = readFlagsFromQuery(window.location.search);
  const hasAny = devMode !== undefined || pttHidden !== undefined || demoMode !== undefined;
  if (hasAny) {
    dispatch(setFlags({
      ...(devMode !== undefined && { devMode }),
      ...(pttHidden !== undefined && { pttHidden }),
      ...(demoMode !== undefined && { demoMode })
    }));
    const url = new URL(window.location.href);
    url.searchParams.delete('devMode');
    url.searchParams.delete('pttHidden');
    url.searchParams.delete('demoMode');
    window.history.replaceState({}, '', url.toString());
  }
};

export const useFlagQuerySync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { devMode, pttHidden, demoMode } = readFlagsFromQuery(location.search);
    const hasAny = devMode !== undefined || pttHidden !== undefined || demoMode !== undefined;
    if (!hasAny) return;

    const payload: any = {};
    if (devMode !== undefined) payload.devMode = devMode;
    if (pttHidden !== undefined) payload.pttHidden = pttHidden;
    if (demoMode !== undefined) payload.demoMode = demoMode;
    dispatch(setFlags(payload));

    // strip only these params; keep others intact
    const url = new URL(window.location.href);
    url.searchParams.delete('devMode');
    url.searchParams.delete('pttHidden');
    url.searchParams.delete('demoMode');
    navigate({ pathname: url.pathname, search: url.search, hash: url.hash }, { replace: true });
  }, [location.search, dispatch, navigate]);
};
